import { useState } from "react";
import { api } from "@/lib/api";
import SectionHeader from "@/components/SectionHeader";
import { toast } from "sonner";
import { Mail, MapPin, Phone } from "lucide-react";

const inputCls = "w-full bg-background border border-input px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contacts", form);
      toast.success("Thanks for reaching out — we'll respond soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-5 gap-12">
        <div className="lg:col-span-2">
          <SectionHeader eyebrow="Get In Touch" title="Have a question? We're listening." />
          <p className="text-foreground/85 leading-relaxed mb-8">
            Whether you'd like to share an idea, raise a concern, or invite Linda to a community event — drop us a line. The campaign team responds to every message.
          </p>
          <ul className="space-y-5 text-sm">
            <li className="flex gap-3 items-start">
              <div className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center shrink-0"><MapPin size={18} /></div>
              <div><div className="font-bold text-primary">South Lebanon, OH</div><div className="text-muted-foreground">Friends of Linda Burke HQ</div></div>
            </li>
            <li className="flex gap-3 items-start">
              <div className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center shrink-0"><Mail size={18} /></div>
              <div><div className="font-bold text-primary">lsburke4455@aol.com</div><div className="text-muted-foreground">General inquiries</div></div>
            </li>
            <li className="flex gap-3 items-start">
              <div className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center shrink-0"><Phone size={18} /></div>
              <div><div className="font-bold text-primary">(513) 280-0929</div><div className="text-muted-foreground">Campaign office</div></div>
            </li>
          </ul>
        </div>

        <form onSubmit={submit} className="lg:col-span-3 bg-card border border-border p-8 md:p-10 space-y-5" data-testid="contact-form">
          <div className="grid md:grid-cols-2 gap-4">
            <label>
              <span className="block text-xs uppercase tracking-[0.18em] font-bold text-primary mb-2">Name <span className="text-accent">*</span></span>
              <input required className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
            </label>
            <label>
              <span className="block text-xs uppercase tracking-[0.18em] font-bold text-primary mb-2">Email <span className="text-accent">*</span></span>
              <input type="email" required className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
            </label>
          </div>
          <label className="block">
            <span className="block text-xs uppercase tracking-[0.18em] font-bold text-primary mb-2">Subject</span>
            <input className={inputCls} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject" />
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-[0.18em] font-bold text-primary mb-2">Message <span className="text-accent">*</span></span>
            <textarea required rows="6" className={inputCls} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" />
          </label>
          <button
            type="submit"
            disabled={submitting}
            data-testid="contact-submit"
            className="bg-accent text-accent-foreground px-7 py-3.5 font-bold uppercase tracking-wider hover:bg-[#8A1333] transition-colors disabled:opacity-60"
          >
            {submitting ? "Sending…" : "Send Message"}
          </button>
        </form>
      </div>
    </div>
  );
}
